import Avatar from "@mui/material/Avatar";
import Guid from "common/values/guid/guid";
import IndividualAPIService, { 
  IndividualAvatarNotFoundError 
} from "marketplace/entities/individual/api/individual-api-service";
import { useCallback, useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import Session from "users/session/session";
import CircularProgress from "@mui/material/CircularProgress";

const StyledAvatar = styled(Avatar)<{ disabled?: boolean }>(({ disabled }) => ({
  opacity: disabled ? 0.5 : 1
}));
const LoadingProgress = styled(CircularProgress)(({ theme }) => ({
  color: theme.palette.grey[theme.palette.mode === 'light' ? 200 : 700],
  height: '75%',
  width: '75%'
}));

type IndividualAvatarProps = {
  className?: string;
  avatarId: Guid | undefined | null;
  individualId: Guid | undefined | null;
  disabled?: boolean;
  session: Session;
}

export default function IndividualAvatar(props: Readonly<IndividualAvatarProps>) {
  const { 
    className,
    avatarId,
    individualId,
    disabled,
    session
  } = props;

  const [avatar, setAvatar] = useState<string>();
  const [imageLoading, setImageLoading] = useState<boolean>(true);

  const getAvatar = useCallback(async () => {
    if (!avatarId || !individualId) {
      setImageLoading(false);
      return;
    }

    try {
      const service = new IndividualAPIService(session);
      const abortController = new AbortController();
      const base64Avatar = await service.getIndividualAvatar(individualId, abortController);
      setAvatar(base64Avatar);
    } catch (error) {
      if (error instanceof IndividualAvatarNotFoundError) {
        console.debug(`No avatar found for individual ${individualId.value}`);
        return;
      }
      throw error;
    } finally {
      setImageLoading(false);
    }
  }, [avatarId, individualId, session]);

  useEffect(() => {
    void getAvatar();
  }, []);

  return (
    <StyledAvatar 
      className={className}
      src={avatar}
      disabled={disabled}
      slotProps={{
        img: {
          onLoad: () => setImageLoading(false),
          onError: () => setImageLoading(false)
        }
      }}
    >
      {imageLoading && <LoadingProgress disableShrink />}
    </StyledAvatar>
  );
}
